/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState, useEffect } from 'react';

export default function FiltersSection({
  theme,
  displayCategories,
  selectedCategory,
  selectedSubCategory,
  handleCategoryClick,
  search,
  setSearch,
  sortBy,
  setSortBy,
  viewMode,
  setViewMode,
}) {

  const safeTheme = theme || { primary: "#A855F7", accent: "#FCD34D" };
  const cats = Array.isArray(displayCategories) ? displayCategories : [];
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  useEffect(() => {
    if (!sortMenuOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.sort-dropdown-container')) {
        setSortMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [sortMenuOpen]);

  const sortOptions = [
    { value: 'popular', label: 'الأكثر شهرة' },
    { value: 'price-low', label: 'السعر: من الأقل للأعلى' },
    { value: 'price-high', label: 'السعر: من الأعلى للأقل' },
    { value: 'alphabetical', label: 'الاسم: أ - ي' },
    { value: 'newest', label: 'الأحدث أولاً' },
    { value: 'big_discount', label: 'خصم كبير' },
  ];

  const currentSortLabel =
    sortOptions.find(opt => opt.value === sortBy)?.label || 'الترتيب الافتراضي';

  const hasActiveFilters =
    search || selectedCategory !== 0 || selectedSubCategory;

  return (
    <section className="relative w-full flex justify-center px-3 sm:px-4 py-3 sm:py-6">
      <div className="w-full max-w-6xl space-y-6">

        {/* ================= Search & Controls Bar ================= */}
        <div className="relative z-[300] flex flex-col lg:flex-row gap-3 lg:gap-4 items-stretch lg:items-center bg-white/5 backdrop-blur-md p-3 lg:p-4 rounded-3xl border border-white/10">

          {/* Search Bar - Modern with icon (from Theme 1 Header idea) */}
          <div className="relative flex-1 w-full group">
            <div className="absolute left-5 top-1/2 -translate-y-1/2 text-white/30 group-focus-within:text-purple-400 transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
            <input
              type="text"
              placeholder="ابحث عن منتج، ماركة، أو فئة..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-black border border-white/10 text-white pl-12 pr-5 py-3.5 rounded-2xl focus:border-purple-500/40 focus:ring-4 focus:ring-purple-500/10 transition-all outline-none text-sm placeholder:text-white/40"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex items-center gap-3 w-full lg:w-auto">
            {/* Sort Dropdown */}
            <div className="relative z-[400] sort-dropdown-container col-span-2 sm:col-span-2 lg:col-auto flex-1 lg:w-56">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSortMenuOpen(!sortMenuOpen);
                }}
                className="w-full bg-black/40 border border-white/10 text-white px-4 lg:px-5 py-3 rounded-2xl flex justify-between items-center hover:border-white/30 transition-all text-sm"
              >
                <span className="font-medium truncate mr-2">
                  {currentSortLabel}
                </span>
                <svg
                  className={`w-4 h-4 transition-transform shrink-0 ${sortMenuOpen ? "rotate-180" : ""} text-white`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {sortMenuOpen && (
                <div
                  className="fixed inset-0 z-[400] bg-black/50 backdrop-blur-sm lg:hidden"
                  onClick={() => setSortMenuOpen(false)}
                />
              )}

              {sortMenuOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 w-full min-w-[220px] bg-black/95 border border-white/10 rounded-2xl shadow-2xl z-[700] backdrop-blur-xl max-h-[60vh] overflow-y-auto">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSortBy(opt.value);
                        setSortMenuOpen(false);
                      }}
                      className={`w-full text-right px-5 py-3 text-sm transition-all flex items-center justify-between
                        ${sortBy === opt.value
                          ? "text-white bg-white/15"
                          : "text-white/60 hover:text-white hover:bg-white/5"
                        }`}
                    >
                      {opt.label}
                      {sortBy === opt.value && (
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: safeTheme.primary }}></div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* View Mode Toggle (from Theme 1 ProductsList idea) */}
            <div className="flex items-center bg-black/40 border border-white/10 p-1.5 rounded-2xl gap-1 justify-center">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-xl transition-all ${viewMode === "grid" ? "bg-white/15 text-white shadow-lg" : "text-white/60 hover:text-white"}`}
                title="عرض شبكي"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-xl transition-all ${viewMode === "list" ? "bg-white/15 text-white shadow-lg" : "text-white/60 hover:text-white"}`}
                title="عرض قائمة"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6"></line>
                  <line x1="8" y1="12" x2="21" y2="12"></line>
                  <line x1="8" y1="18" x2="21" y2="18"></line>
                  <line x1="3" y1="6" x2="3.01" y2="6"></line>
                  <line x1="3" y1="12" x2="3.01" y2="12"></line>
                  <line x1="3" y1="18" x2="3.01" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setSearch("");
                  handleCategoryClick(0);
                  setSortBy("popular");
                }}
                className="p-3 rounded-2xl border border-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-all group"
                title="مسح الكل"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ================= Categories (from Theme 1 CategoriesBar idea: Horizontal Scroll) ================= */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-white/70">تصفح حسب الفئات</h3>
            {selectedCategory !== 0 && (
              <button
                onClick={() => handleCategoryClick(0)}
                className="text-xs text-purple-400 hover:text-purple-300 font-medium"
              >
                عرض الكل
              </button>
            )}
          </div>

          <div className="relative group">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:auto-cols-max lg:flex overflow-x-auto no-scrollbar pb-2 gap-3 scroll-smooth">
              {cats.map((cat) => {
                const isActive = Number(selectedCategory) === Number(cat.id);

                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.id)}
                    className={`flex items-center gap-2.5 px-4 sm:px-5 py-3 rounded-2xl border whitespace-nowrap transition-all duration-300 transform hover:scale-105
                      ${isActive
                        ? "shadow-lg shadow-purple-500/20"
                        : "hover:bg-white/5"
                      }`}
                    style={{
                      backgroundColor: isActive ? safeTheme.primary : "rgba(255,255,255,0.03)",
                      borderColor: isActive ? safeTheme.primary : "rgba(255,255,255,0.08)",
                      color: isActive ? "#fff" : "rgba(255,255,255,0.6)"
                    }}
                  >
                    {cat.icon && (
                      <span className="text-lg opacity-90 text-white">{cat.icon}</span>
                    )}
                    <span className="font-bold text-sm">{cat.name}</span>
                    {/* Show count for all categories if count > 0 */}
                    {cat.count > 0 && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${isActive ? "bg-white/20 text-white" : "bg-white/5 text-white/40"}`}>
                        {cat.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Fade indicators for scroll */}
            <div className="hidden lg:block absolute right-0 top-0 bottom-2 w-12 bg-gradient-to-l from-black/20 to-transparent pointer-events-none rounded-r-2xl"></div>
            <div className="hidden lg:block absolute left-0 top-0 bottom-2 w-12 bg-gradient-to-r from-black/20 to-transparent pointer-events-none rounded-l-2xl"></div>
          </div>

          {/* Sub Categories - Nested Hierarchy Style */}
          {Boolean(selectedCategory) && Number(selectedCategory) !== 0 && cats.find((c) => Number(c.id) === Number(selectedCategory))?.subCategories?.length > 0 && (
            <div className="relative pt-4 pr-6 flex flex-wrap gap-2 animate-in fade-in slide-in-from-top-2 duration-300 border-r border-white/10 mr-4">
              {/* Connector line indicator */}
              <div className="absolute -top-4 right-0 bottom-0 w-px bg-gradient-to-b from-purple-500/50 to-transparent"></div>

              {cats
                .find((c) => Number(c.id) === Number(selectedCategory))
                ?.subCategories?.map((sub) => {
                  const isSubActive = Number(selectedSubCategory) === Number(sub.id);

                  return (
                    <button
                      key={sub.id}
                      onClick={() => handleCategoryClick(selectedCategory, sub.id)}
                      className="px-4 py-2 rounded-xl text-xs font-semibold border transition-all flex items-center gap-2 group"
                      style={{
                        backgroundColor: isSubActive
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(255,255,255,0.02)",
                        borderColor: isSubActive
                          ? safeTheme.primary
                          : "rgba(255,255,255,0.05)",
                        color: isSubActive ? "white" : "rgba(255,255,255,0.4)",
                      }}
                    >
                      {isSubActive && <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: safeTheme.primary }}></div>}
                      {sub.name}
                      {sub.count > 0 && (
                        <span className="opacity-40 text-[10px] group-hover:opacity-100 transition-opacity">({sub.count})</span>
                      )}
                    </button>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      <style jsx="true">{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}





